import { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, Activity, Clock, History as HistoryIcon, Trophy, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MobileHeader from '../components/MobileHeader';
import TradingChart from '../components/trading/TradingChart';
import TradingPairSelector from '../components/trading/TradingPairSelector';
import OrderForm from '../components/trading/OrderForm';
import { useCryptoData } from '../hooks/useCryptoData';
import { tradingAPI } from '../services/api';

import { fmtFixed, fmtNumber } from '../utils/format';
const Trading = () => {
  const navigate = useNavigate();
  const { cryptos, loading } = useCryptoData(20);
  const [selectedMode, setSelectedMode] = useState('spot');
  const [activeTab, setActiveTab] = useState('trade');
  const [selectedPair, setSelectedPair] = useState('BTC/USDT');
  const [pairData, setPairData] = useState({ symbol: 'BTC', price: 43250.50 });
  const [activeOrders, setActiveOrders] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const handlePairSelect = (pair) => {
    setSelectedPair(pair.pair);
    setPairData({ symbol: pair.symbol, price: pair.price });
  };

  // Fetch active orders
  const fetchActiveOrders = async () => {
    try {
      setLoadingOrders(true);
      const orders = await tradingAPI.getActiveOrders();
      setActiveOrders(orders);
    } catch (error) {
      console.error('Error fetching active orders:', error);
      toast.error('Failed to load active orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fetch order history
  const fetchOrderHistory = async () => {
    try {
      setLoadingOrders(true);
      const orders = await tradingAPI.getOrderHistory();
      setOrderHistory(orders);
    } catch (error) {
      console.error('Error fetching order history:', error);
      toast.error('Failed to load order history');
    } finally {
      setLoadingOrders(false);
    }
  };

  // Cancel an order
  const handleCancelOrder = async (orderId) => {
    try {
      await tradingAPI.cancelOrder(orderId);
      toast.success('Order cancelled successfully');
      fetchActiveOrders(); // Refresh active orders
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order');
    }
  };

  // Fetch orders when tab changes
  useEffect(() => {
    if (activeTab === 'active') {
      fetchActiveOrders();
    } else if (activeTab === 'history') {
      fetchOrderHistory();
    }
  }, [activeTab]);

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navbar />
      <MobileHeader />

      <div className="pt-20 sm:pt-24 pb-20 sm:pb-12 px-3 sm:px-4 md:px-6">
        <div className="container mx-auto max-w-7xl">

          {/* Hub Section */}
          <div className="mb-4 sm:mb-6 glow-card p-4 sm:p-6 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-6 w-6 text-gray-400" />
              </button>
              <div className="flex-1 ml-4">
                <h2 className="text-2xl font-bold text-white mb-1">Hub</h2>
                <p className="text-sm text-gray-400">All trading operations in one place</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-yellow-400">$3,268,886</div>
                <p className="text-xs text-gray-500">Live prices via CoinGecko</p>
              </div>
            </div>
          </div>

          {/* Trading Mode Selection */}
          <div className="mb-4 sm:mb-6 glow-card p-3 sm:p-4 rounded-xl">
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setSelectedMode('spot')}
                className={`py-4 rounded-lg font-bold transition-all ${
                  selectedMode === 'spot'
                    ? 'bg-gradient-to-r from-purple-600 to-purple-800 text-white shadow-lg'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
                }`}
              >
                <TrendingUp className="h-5 w-5 mx-auto mb-2" />
                <div className="text-sm">Spot Trading</div>
              </button>
              <button
                onClick={() => setSelectedMode('margin')}
                className={`py-4 rounded-lg font-bold transition-all ${
                  selectedMode === 'margin'
                    ? 'bg-gradient-to-r from-purple-600 to-purple-800 text-white shadow-lg'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
                }`}
              >
                <Activity className="h-5 w-5 mx-auto mb-2" />
                <div className="text-sm">Margin Trading</div>
              </button>
              <button
                onClick={() => setSelectedMode('futures')}
                className={`py-4 rounded-lg font-bold transition-all ${
                  selectedMode === 'futures'
                    ? 'bg-gradient-to-r from-purple-600 to-purple-800 text-white shadow-lg'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
                }`}
              >
                <Clock className="h-5 w-5 mx-auto mb-2" />
                <div className="text-sm">Futures</div>
              </button>
            </div>
          </div>

          {/* Activity Tabs */}
          <div className="mb-4 sm:mb-6 glow-card p-3 sm:p-4 rounded-xl">
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setActiveTab('trade')}
                className={`py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'trade'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
                }`}
              >
                Trade
              </button>
              <button
                onClick={() => setActiveTab('active')}
                className={`py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'active'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-3 rounded-lg font-semibold transition-all ${
                  activeTab === 'history'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
                }`}
              >
                History
              </button>
            </div>
          </div>

          {/* Main Trading Content */}
          {activeTab === 'trade' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Left Column - Trading Pairs */}
              <div>
                <TradingPairSelector
                  selectedPair={selectedPair}
                  onSelectPair={handlePairSelect}
                />
              </div>

              {/* Middle Column - Chart */}
              <div className="lg:col-span-2">
                <TradingChart
                  selectedPair={selectedPair}
                  currentPrice={pairData.price}
                />
                
                {/* Order Form Below Chart on Desktop */}
                <div className="mt-6">
                  <OrderForm
                    selectedPair={selectedPair}
                    currentPrice={pairData.price}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Active Orders Tab */}
          {activeTab === 'active' && (
            <div className="glow-card p-6 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-4">Active Orders</h3>
              {loadingOrders ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-gray-400 mt-4">Loading orders...</p>
                </div>
              ) : activeOrders.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No active orders</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeOrders.map((order) => (
                    <div key={order.id} className="bg-gray-800/50 p-4 rounded-lg hover:bg-gray-800 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            order.side === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {order.side.toUpperCase()}
                          </span>
                          <span className="text-white font-semibold">{order.pair}</span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">
                            {order.order_type.toUpperCase()}
                          </span>
                        </div>
                        <button
                          onClick={() => handleCancelOrder(order.id)}
                          className="p-1 hover:bg-red-500/20 rounded transition-colors"
                          title="Cancel order"
                        >
                          <X className="h-5 w-5 text-red-400" />
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 text-xs">Amount</div>
                          <div className="text-white font-semibold">{order.amount}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">Price</div>
                          <div className="text-white font-semibold">${order.price ? fmtNumber(order.price, 2) : 'Market'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">Status</div>
                          <div className="text-yellow-400 font-semibold capitalize">{order.status}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-500 text-xs">Created</div>
                          <div className="text-gray-400 text-xs">{formatDate(order.created_at)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="glow-card p-6 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-4">Order History</h3>
              {loadingOrders ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-gray-400 mt-4">Loading history...</p>
                </div>
              ) : orderHistory.length === 0 ? (
                <div className="text-center py-12">
                  <HistoryIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No order history</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orderHistory.map((order) => (
                    <div key={order.id} className="bg-gray-800/50 p-4 rounded-lg hover:bg-gray-800 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            order.side === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {order.side.toUpperCase()}
                          </span>
                          <span className="text-white font-semibold">{order.pair}</span>
                          <span className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-400">
                            {order.order_type.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(order.completed_at || order.updated_at)}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 text-xs">Amount</div>
                          <div className="text-white font-semibold">{order.filled_amount || order.amount}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">Price</div>
                          <div className="text-white font-semibold">${(order.executed_price != null ? fmtFixed(order.executed_price, 2) : undefined) || (order.price != null ? fmtFixed(order.price, 2) : undefined) || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-xs">Total</div>
                          <div className="text-yellow-400 font-semibold">${fmtFixed(order.total, 2)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-500 text-xs">Status</div>
                          <div className={`font-semibold capitalize ${
                            order.status === 'completed' ? 'text-green-400' : 
                            order.status === 'cancelled' ? 'text-yellow-400' : 
                            'text-red-400'
                          }`}>
                            {order.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Trading;
